import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Loader2, Download, Lock, Sparkles, Sigma, Clock3, AlertCircle, BarChart2, Grid3x3, FileText } from "lucide-react";
import api from "@/lib/api";
import { effectiveIsClinician } from "@/lib/effectiveClinician";
import { toast } from "sonner";
import ProfileMatrixSection from "./ProfileMatrixSection";
import DerivedInsightsPanel from "./DerivedInsightsPanel";
import LifePatternsClinicalCard from "./LifePatternsClinicalCard";
import LeaderboardClinicalCard from "./LeaderboardClinicalCard";

// ===== Static label maps (Italian) =====
const SAMPLING_LABELS = {
  random: "Random",
  scheduled: "Orario fisso",
  self_initiated: "Spontaneo",
  "—": "Non taggato",
};
const FRAME_LABELS = {
  prompt_time: "Momento del ping",
  now: "Adesso",
  unspecified: "Non specificato",
  "—": "Non specificato",
};
const TIER_LABELS = {
  momentary: "Momentary (<15min)",
  delayed: "Delayed (<60min)",
  retrospective: "Retrospective (>60min)",
  "—": "Senza ping (spontaneo)",
};
const TIER_COLORS = {
  momentary: "bg-emerald-100 text-emerald-800 border-emerald-300",
  delayed: "bg-amber-100 text-amber-800 border-amber-300",
  retrospective: "bg-rose-100 text-rose-800 border-rose-300",
  "—": "bg-slate-100 text-slate-700 border-slate-300",
};
const LOAD_LABELS = { leggero: "Leggero", medio: "Medio", pesante: "Pesante" };
const PLEAS_LABELS = {
  molto_spiacevole: "Molto spiacevole",
  spiacevole: "Spiacevole",
  neutra: "Neutra",
  piacevole: "Piacevole",
  molto_piacevole: "Molto piacevole",
};
const VOL_LABELS = { per_niente: "Per niente", in_parte: "In parte", abbastanza: "Abbastanza (legacy)", totalmente: "Totalmente" };
const SOC_QUAL_LABELS = { positiva: "Positiva", neutra: "Neutra", tesa: "Tesa", assente: "Assente" };
const ACTIVITY_LABELS = {
  lavoro: "Lavoro", studio: "Studio", riposo: "Riposo", riposo_relax: "Riposo (legacy)",
  svago_online: "Svago online", vita_sociale: "Vita sociale", socialita: "Socialità (legacy)",
  sport: "Sport", cura_personale: "Cura personale", cura_casa: "Cura della casa",
  creativita_hobby: "Creatività/Hobby", cura_altri: "Cura degli altri",
  commissioni: "Commissioni", altro: "Altro", pausa: "Pausa (legacy)",
};
const SOCIAL_CTX_LABELS = {
  solo: "Solo", amici: "Amici", partner: "Partner", famiglia: "Famiglia",
  partner_famiglia: "Partner/Famiglia (legacy)", colleghi: "Colleghi",
  superiori: "Superiori", clienti: "Clienti/Pazienti", altri: "Altri",
  con_altri: "Con altri (legacy)",
};
const TOD_LABELS = { mattina: "Mattina", pomeriggio: "Pomeriggio", sera: "Sera" };

const DIM_COLORS = {
  load: ["#86efac", "#fbbf24", "#f87171"],          // green / amber / red
  pleasantness: ["#dc2626", "#f87171", "#94a3b8", "#86efac", "#16a34a"], // dark red → red → gray → green → dark green
  voluntariness: ["#f87171", "#fbbf24", "#86efac"],
  social_quality: ["#86efac", "#94a3b8", "#f87171", "#cbd5e1"],
};

// ===== Sub-components =====

const DistributionBar = ({ labelMap, counts, palette }) => {
  const entries = Object.entries(counts);
  const total = entries.reduce((s, [, n]) => s + n, 0);
  return (
    <div className="space-y-1.5" data-testid="distribution-bar">
      {entries.map(([key, n], i) => {
        const pct = total > 0 ? Math.round((n / total) * 100) : 0;
        const label = labelMap[key] || key;
        const color = palette[i % palette.length];
        return (
          <div key={key} className="flex items-center gap-2 text-xs">
            <span className="w-24 sm:w-28 text-muted-foreground shrink-0">{label}</span>
            <div className="flex-1 h-4 bg-muted rounded-sm overflow-hidden">
              <div
                className="h-full transition-all"
                style={{ width: `${pct}%`, backgroundColor: color }}
              />
            </div>
            <span className="w-12 text-right tabular-nums">{n} ({pct}%)</span>
          </div>
        );
      })}
    </div>
  );
};

const CrossTab = ({ table, rowTotals, rowLabels, colLabels, rowKey, colKey }) => {
  const rowOrder = Object.keys(table).filter((r) => rowTotals[r] > 0);
  const colOrder = colLabels ? Object.keys(colLabels) : [];

  if (rowOrder.length === 0) {
    return (
      <p className="text-[11px] text-muted-foreground italic">
        Nessun dato in questa combinazione ancora.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto" data-testid={`crosstab-${rowKey}-${colKey}`}>
      <table className="w-full text-[11px] border-collapse">
        <thead>
          <tr>
            <th className="text-left font-medium text-muted-foreground py-1 pr-2">·</th>
            {colOrder.map((c) => (
              <th key={c} className="text-center font-medium text-muted-foreground py-1 px-1.5">
                {colLabels[c]}
              </th>
            ))}
            <th className="text-center font-semibold text-muted-foreground py-1 pl-2">N</th>
          </tr>
        </thead>
        <tbody>
          {rowOrder.map((r) => (
            <tr key={r} className="border-t border-muted">
              <td className="py-1.5 pr-2 font-medium">{rowLabels[r] || r}</td>
              {colOrder.map((c) => {
                const n = table[r]?.[c] ?? 0;
                const rt = rowTotals[r] || 1;
                const pct = Math.round((n / rt) * 100);
                const intensity = Math.min(1, n / Math.max(1, rt));
                const bg = n > 0
                  ? `rgba(99, 102, 241, ${0.08 + intensity * 0.45})`
                  : "transparent";
                return (
                  <td
                    key={c}
                    className="text-center py-1.5 px-1.5 tabular-nums"
                    style={{ backgroundColor: bg }}
                  >
                    {n > 0 ? (
                      <>
                        <div className="font-semibold">{n}</div>
                        <div className="text-[9px] text-muted-foreground">{pct}%</div>
                      </>
                    ) : (
                      <span className="text-muted-foreground/40">·</span>
                    )}
                  </td>
                );
              })}
              <td className="text-center py-1.5 pl-2 font-semibold tabular-nums">
                {rowTotals[r]}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const SummaryTile = ({ value, label, testid }) => (
  <Card className="border" data-testid={testid}>
    <CardContent className="p-2 text-center">
      <p className="text-xl font-bold tabular-nums">{value}</p>
      <p className="text-[10px] text-muted-foreground leading-tight">{label}</p>
    </CardContent>
  </Card>
);

const BadgeRow = ({ counts, labelMap, palette }) => {
  const entries = Object.entries(counts).filter(([, n]) => n > 0);
  if (entries.length === 0) {
    return <p className="text-[11px] text-muted-foreground italic">Nessun dato.</p>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {entries.map(([k, n]) => (
        <Badge
          key={k}
          variant="outline"
          className={`text-[11px] py-1 px-2 ${palette?.[k] || "border-muted-foreground/30"}`}
        >
          {labelMap[k] || k}: <span className="font-bold ml-1">{n}</span>
        </Badge>
      ))}
    </div>
  );
};

// ===== Main panel =====
// ===== EMA QUALITY TIPS — discursive recommendations, no jargon =====
// 2026-06 (Laura): la qualità EMA viene tradotta in consigli azionabili,
// visibili a TUTTI (non solo al clinico). Sostituisce, nel pannello user,
// le metriche tecniche di ritardo/sampling.
const EmaQualityTipsCard = ({ summary, responseDelay }) => {
  if (!summary) return null;

  const analyzed = summary.analyzed_count || 0;
  const byTier = (responseDelay && responseDelay.by_tier) || {};
  const momentary = byTier.momentary || 0;
  const delayed = byTier.delayed || 0;
  const retro = byTier.retrospective || 0;
  const tierTotal = momentary + delayed + retro;
  const bySampling = summary.by_sampling_type || {};
  const spontaneous = bySampling.self_initiated || 0;
  const random = bySampling.random || 0;
  const scheduled = bySampling.scheduled || 0;
  const samplingTotal = spontaneous + random + scheduled;

  const tips = [];

  if (analyzed < 7) {
    tips.push({
      icon: "🌱",
      title: "Hai appena iniziato",
      body: "Continua a fare check-in: anche 3-4 al giorno bastano per iniziare a vedere pattern affidabili. Le prime scoperte si stabilizzano intorno ai 20-30 check-in.",
    });
  } else {
    if (tierTotal >= 3 && retro / Math.max(1, tierTotal) >= 0.4) {
      tips.push({
        icon: "⏰",
        title: "Prova a rispondere ai promemoria più rapidamente",
        body: "Quando arriva una notifica, prova a fare il check-in entro 15 minuti. Più il tempo passa, più la memoria del momento sfuma e le tue risposte diventano meno precise.",
      });
    }

    if (samplingTotal >= 5 && (random + scheduled) === 0) {
      tips.push({
        icon: "🔔",
        title: "Attiva i promemoria casuali",
        body: "Stai facendo check-in solo quando ti viene in mente: questo tende a sovra-rappresentare i momenti emotivamente intensi. Attivare i ping casuali dalle Impostazioni ti aiuta a catturare anche i momenti più neutri, che sono i più informativi.",
      });
    }

    // 2026-06: Tip — finestra oraria sotto-rappresentata (almeno 10 check-in totali)
    const byTod = summary.by_time_of_day || {};
    const todTotal = (byTod.mattina || 0) + (byTod.pomeriggio || 0) + (byTod.sera || 0);
    if (analyzed >= 10 && todTotal >= 8) {
      const slots = [
        { key: "mattina", n: byTod.mattina || 0, label: "al mattino" },
        { key: "pomeriggio", n: byTod.pomeriggio || 0, label: "al pomeriggio" },
        { key: "sera", n: byTod.sera || 0, label: "alla sera" },
      ];
      slots.sort((a, b) => a.n - b.n);
      const lowest = slots[0];
      const highest = slots[2];
      // Suggerisci solo se la finestra più scarica ha < 50% della più piena
      if (highest.n >= 4 && lowest.n < Math.ceil(highest.n / 2)) {
        tips.push({
          icon: "📅",
          title: `Prova ad aggiungere qualche check-in ${lowest.label}`,
          body: `Hai ${lowest.n} check-in ${lowest.label} (contro ${highest.n} ${highest.label}). Distribuire meglio i check-in nella giornata aiuta a vedere come cambia il tuo stato d'animo nelle diverse fasce orarie.`,
        });
      }
    }

    if (analyzed >= 7 && analyzed < 20 && tips.length === 0) {
      tips.push({
        icon: "📈",
        title: "Sei a buon punto",
        body: `Hai ${analyzed} check-in: le prime scoperte stanno arrivando. Continuando a fare 3-5 check-in al giorno arriverai presto a 20-30, dove i pattern diventano davvero stabili.`,
      });
    }

    if (analyzed >= 20 && tips.length === 0) {
      tips.push({
        icon: "✨",
        title: "Stai facendo un ottimo lavoro",
        body: "I tuoi check-in sono regolari e la qualità del dato è buona. Continua così: i pattern che vedi qui sotto sono ormai stabili.",
      });
    }

    if (tips.length === 0) {
      tips.push({
        icon: "🌿",
        title: "Continua a rispondere ai check-in",
        body: "I tuoi dati stanno crescendo bene. Cerca di rispondere ai promemoria nei tempi che ti chiede l'app, così le scoperte diventeranno sempre più nitide.",
      });
    }
  }

  return (
    <Card className="border bg-gradient-to-br from-stone-50 to-amber-50/30" data-testid="ema-quality-tips">
      <CardContent className="p-3 sm:p-4 space-y-3">
        <h3
          className="text-base sm:text-lg font-semibold text-stone-800 leading-tight"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Per scoperte più precise
        </h3>
        <div className="space-y-3">
          {tips.map((t, i) => (
            <div key={i} className="flex gap-2.5">
              <div className="text-xl shrink-0" aria-hidden="true">{t.icon}</div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-stone-800">{t.title}</p>
                <p className="text-xs text-stone-600 leading-relaxed mt-0.5">{t.body}</p>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};



const InsightsPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [includeV1, setIncludeV1] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingAudit, setDownloadingAudit] = useState(false);
  const [isClinician, setIsClinician] = useState(false);

  useEffect(() => {
    // Fetch clinician flag once
    const refresh = () => api.get("/auth/me")
      .then((r) => setIsClinician(effectiveIsClinician(r.data?.is_clinician)))
      .catch(() => {});
    refresh();
    const onChange = () => refresh();
    window.addEventListener("revela:forced-user-view-changed", onChange);
    return () => window.removeEventListener("revela:forced-user-view-changed", onChange);
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeV1]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/voice-mood/insights?days=365&include_v1=${includeV1}`);
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento insights");
    } finally {
      setLoading(false);
    }
  };

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/voice-mood/export-csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      const filename = `experience_tracking_${new Date().toISOString().slice(0, 10)}.csv`;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV scaricato");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore download CSV");
    } finally {
      setDownloading(false);
    }
  };

  const downloadAuditMd = async () => {
    setDownloadingAudit(true);
    try {
      const res = await api.get(`/voice-mood/insights-audit/export-md?include_v1=${includeV1}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/markdown" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit_insights_${new Date().toISOString().slice(0, 10)}.md`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Audit Markdown scaricato");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore download audit");
    } finally {
      setDownloadingAudit(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8" data-testid="insights-loading">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!data) return null;

  const { summary, ema_distributions, response_delay, cross_tabs, correlations_unlocked, n_until_unlock, correlation_threshold } = data;
  const analyzed = summary.analyzed_count;
  const tooFewForCrossTabs = analyzed < 5;

  return (
    <div className="space-y-4" data-testid="insights-panel">
      {/* ===== EMA quality TIPS (discursive, for everyone) =====
          2026-06: Laura request — niente jargon, solo consigli azionabili
          basati sui dati di compliance EMA. */}
      <EmaQualityTipsCard summary={summary} responseDelay={response_delay} />

      {/* ===== 0. DERIVED INDICATORS (wellbeing, weekly, karasek, sdt, trend) ===== */}
      <DerivedInsightsPanel />

      {/* ===== APPROFONDIMENTI CLINICI (solo Laura / admin) =====
          Tutto il jargon EMA, riepilogo dataset, unlock counter, MATRICE PROFILI
          esperienziali sta qui dentro, collassato di default per non confondere
          la lettura discorsiva. */}
      {isClinician && (
        <details className="group" data-testid="clinical-deep-dive">
          <summary className="cursor-pointer list-none flex items-center justify-between gap-2 py-2.5 px-3 rounded-md border border-dashed border-indigo-300 bg-indigo-50/40 hover:bg-indigo-100/50 transition-colors">
            <span className="text-xs sm:text-sm font-medium text-indigo-900 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" />
              Approfondimenti clinici (solo clinico)
            </span>
            <span className="text-[10px] text-indigo-700 group-open:rotate-180 transition-transform">▾</span>
          </summary>

          <div className="space-y-3 pt-3">
            {/* Graduatoria completa per piacevolezza — 2026-02-16 (Laura).
                Sostituisce le vecchie bar charts rimosse nella refactor "Wrapped".
                Tutte le categorie ordinate, con SD e delta dalla media. */}
            <LeaderboardClinicalCard />

            {/* Pattern di vita — 2026-XX (Laura): incrocio Volontarietà × Piacevolezza
                per coppia (attività + contesto sociale). Scatter + 4 quadranti +
                tabella completa. Le narrative discorsive di queste stesse analisi
                sono già visibili agli utenti nelle wrapped insights. */}
            <LifePatternsClinicalCard />

            {/* Matrice profili — 2026-06: spostata qui da posizione visibile a tutti.
                Solo Laura la vede ora, le beta tester non vedono più la sezione. */}
            <ProfileMatrixSection includeV1={includeV1} />

            {/* Toggle v1 + audit MD */}
            <Card className="border border-indigo-200 bg-indigo-50/30">
              <CardContent className="p-3 space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[11px] font-medium">Includi dati v1 (pre-redesign)</p>
                    <p className="text-[10px] text-muted-foreground leading-tight">
                      {summary.total_v1} entry v1 · {summary.total_v2} entry v2
                    </p>
                  </div>
                  <Switch
                    checked={includeV1}
                    onCheckedChange={setIncludeV1}
                    data-testid="insights-include-v1-toggle"
                  />
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={downloadAuditMd}
                  disabled={downloadingAudit}
                  className="w-full text-xs h-8"
                  data-testid="insights-export-audit"
                >
                  {downloadingAudit ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5 mr-1" />}
                  Audit MD
                </Button>
              </CardContent>
            </Card>

            {/* Qualità EMA · ritardo di risposta */}
            <Card className="border" data-testid="insights-delay">
              <CardHeader className="pb-1.5 px-3 pt-3">
                <CardTitle className="text-xs sm:text-sm flex items-center gap-1.5">
                  <Clock3 className="w-4 h-4" /> Qualità EMA · ritardo di risposta
                </CardTitle>
              </CardHeader>
              <CardContent className="px-3 pb-3 space-y-2">
                <BadgeRow
                  counts={response_delay.by_tier}
                  labelMap={TIER_LABELS}
                  palette={TIER_COLORS}
                />
                {response_delay.with_prompt_count > 0 ? (
                  <p className="text-[11px] text-muted-foreground">
                    {response_delay.with_prompt_count} check-in linkati a un ping ·
                    ritardo medio <span className="font-semibold tabular-nums">{response_delay.avg_minutes} min</span> ·
                    max <span className="font-semibold tabular-nums">{response_delay.max_minutes} min</span>.
                  </p>
                ) : (
                  <p className="text-[11px] text-muted-foreground italic">
                    Nessun check-in linkato a un ping nel dataset selezionato (tutti spontanei o senza ping).
                  </p>
                )}
                <p className="text-[10px] text-muted-foreground/80 leading-relaxed pt-1 border-t">
                  <strong>Lettura clinica:</strong> idealmente la maggior parte dovrebbe essere <em>momentary</em>.
                  Una prevalenza di <em>retrospective</em> indica bassa compliance EMA — considera di rivedere fasce orarie o numero di ping al giorno.
                </p>
              </CardContent>
            </Card>

            {/* Riepilogo dataset */}
            <Card className="border" data-testid="insights-summary">
              <CardHeader className="pb-1.5 px-3 pt-3">
                <CardTitle className="text-xs sm:text-sm flex items-center gap-1.5">
                  <Sigma className="w-4 h-4" /> Riepilogo dataset
                  {includeV1 && <Badge variant="secondary" className="text-[9px] ml-1">v1 + v2</Badge>}
                  {!includeV1 && <Badge variant="secondary" className="text-[9px] ml-1">solo v2</Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-3 pb-3 space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <SummaryTile value={analyzed} label="In analisi" testid="summary-analyzed" />
                  <SummaryTile value={summary.total_v2} label="v2 (corrette)" testid="summary-v2" />
                  <SummaryTile value={summary.total_v1} label="v1 (legacy)" testid="summary-v1" />
                </div>
                <div>
                  <p className="text-[11px] font-medium text-muted-foreground mb-1.5">Per modalità di campionamento</p>
                  <BadgeRow
                    counts={summary.by_sampling_type}
                    labelMap={SAMPLING_LABELS}
                    palette={{
                      random: "border-rose-300 text-rose-700 bg-rose-50",
                      scheduled: "border-indigo-300 text-indigo-700 bg-indigo-50",
                      self_initiated: "border-teal-300 text-teal-700 bg-teal-50",
                      "—": "border-slate-300 text-slate-600 bg-slate-50",
                    }}
                  />
                </div>
                <div>
                  <p className="text-[11px] font-medium text-muted-foreground mb-1.5">Per frame temporale</p>
                  <BadgeRow counts={summary.by_frame} labelMap={FRAME_LABELS} />
                </div>
              </CardContent>
            </Card>

            {/* Unlock counter */}
            <Card
              className={`border-2 border-dashed ${correlations_unlocked ? "border-emerald-300 bg-emerald-50/50" : "border-indigo-300 bg-indigo-50/30"}`}
              data-testid="insights-unlock-counter"
            >
              <CardContent className="p-3 flex items-center gap-3">
                {correlations_unlocked ? (
                  <>
                    <Sparkles className="w-5 h-5 text-emerald-600 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-emerald-900">Correlazioni statistiche sbloccate</p>
                      <p className="text-[10px] text-emerald-800/80 leading-tight">
                        Hai raggiunto {correlation_threshold}+ entry v2. Si possono ora calcolare chi-square, ICC e confronti per sampling_type.
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5 text-indigo-600 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold text-indigo-900">
                        {n_until_unlock} check-in al primo cruscotto correlazionale
                      </p>
                      <p className="text-[10px] text-indigo-800/80 leading-tight">
                        A N≥{correlation_threshold} (v2) sblocco chi-square, ICC per time-of-day, confronti random vs spontanei.
                      </p>
                      <div className="mt-1.5 h-1.5 rounded-full bg-indigo-100 overflow-hidden">
                        <div
                          className="h-full bg-indigo-500 transition-all"
                          style={{ width: `${Math.min(100, ((correlation_threshold - n_until_unlock) / correlation_threshold) * 100)}%` }}
                        />
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </details>
      )}

      {/* ===== DOWNLOAD button — sempre alla fine (per tutti) ===== */}
      <div className="pt-2">
        <Button
          variant="outline"
          onClick={downloadCsv}
          disabled={downloading}
          className="w-full text-sm h-10 border-stone-300 hover:bg-stone-50"
          data-testid="insights-export-csv"
        >
          {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Download className="w-4 h-4 mr-1.5" />}
          Scarica i tuoi dati (CSV)
        </Button>
        <p className="text-[10px] text-stone-500 text-center mt-1.5">
          Un file con tutti i tuoi check-in (puoi aprirlo con Excel o Numbers).
        </p>
      </div>
    </div>
  );
};

export default InsightsPanel;
